import { graphql } from '@apollo/react-hoc';
import { useQuery } from '@apollo/react-hooks';
import { NetworkStatus } from 'apollo-client';
import gql from 'graphql-tag';
import get from 'lodash/get';

import config from '../../../../config';
import { formatString } from '../../../lib/analytics/gaDimensions';

const DEFAULT_LOCALE = config('defaultLocale');
const DEFAULT_EXPERIENCE_TITLE = config('defaultExperienceTitle');

const {
  loading: LOADING,
  fetchMore: FETCH_MORE,
  refetch: REFETCH
} = NetworkStatus;

const workIsVisible = userId => ({ work }) => {
  if (!work) return false;

  const artistId = get(work, 'artistId');

  const workIsMature = get(work, 'matureContent', false);
  const userOwnsWork = artistId && userId && artistId.toString() === userId;

  const isVisible = !workIsMature && !userOwnsWork;

  return isVisible;
};

const updateQuery = (prevResult, { fetchMoreResult }) => {
  const prevRecommendations = get(
    prevResult,
    'cannedWorkRecommendations.recommendations',
    []
  );
  const newRecommendations = get(
    fetchMoreResult,
    'cannedWorkRecommendations.recommendations',
    []
  );

  return {
    ...fetchMoreResult,
    cannedWorkRecommendations: {
      ...get(fetchMoreResult, 'cannedWorkRecommendations', {}),
      recommendations: [...prevRecommendations, ...newRecommendations]
    }
  };
};

const fetchMoreRecommendations = ({
  from,
  canSize,
  canId,
  fetchMore
}) => limit => {
  if (!(canId && from && limit)) {
    const error = new Error(
      `Attemped fetchMore with invalid arguments: canId:${canId} from:${from} limit:${limit}`
    );
    return Promise.reject(error);
  }

  if (from >= canSize) {
    return Promise.reject(
      new RangeError(
        `from must be less than canSize from:${from} canSize:${canSize} canId:${canId}`
      )
    );
  }

  return fetchMore({ variables: { limit, canId, from }, updateQuery });
};

export const workRecommendationsQuery = (fragments = []) => {
  const workFragments = fragments.filter(({ type }) => type === 'Work');

  return gql`
    query withWorkRecommendations($blacklist: String, $limit: Int, $locale: String!, $experience: guru_Experience, $method: guru_WorkRecommendationMethod, $canId: String, $from: Int) {
      cannedWorkRecommendations: unfilteredRecommendedWorks(limit: $limit, blacklist: $blacklist, experience: $experience, method: $method, canId: $canId, from: $from) {
        recommendations {
          work(locale: $locale) {
            id
            workId
            artistId
            matureContent
            ${workFragments.map(({ name }) => `...${name}`).join('\n')}
          }
        }
        canId
        canSize
        nextFrom
        hasMore
        from
        limit
      }
    }
    ${workFragments.reduce(
      (acc, { fragment }) => gql`
        ${acc}
        ${fragment}
      `,
      ''
    )}
  `;
};

const withWorkRecommendations = Component =>
  graphql(
    workRecommendationsQuery([
      {
        type: 'Work',
        name: Component.displayName,
        fragment: Component.fragment
      }
    ]),
    {
      props: ({
        data: {
          cannedWorkRecommendations,
          error,
          refetch,
          fetchMore,
          variables,
          networkStatus
        },
        ownProps
      }) => {
        const currentUserId = get(ownProps, 'userInfo.userId');
        const items = get(
          cannedWorkRecommendations,
          'recommendations',
          []
        ).filter(workIsVisible(currentUserId));

        const { limit } = variables || {};
        const from = get(cannedWorkRecommendations, 'nextFrom') || limit;
        const canSize = get(cannedWorkRecommendations, 'canSize', 0);
        const canId = get(cannedWorkRecommendations, 'canId');
        const hasMore = get(cannedWorkRecommendations, 'hasMore');

        ownProps.setDimensions({ recommendationUUID: formatString(canId) });

        return {
          ...ownProps,
          loading: [LOADING, REFETCH].includes(networkStatus),
          fetchingMore: networkStatus === FETCH_MORE,
          error,
          refetch,
          hasMore,
          fetchMore: fetchMoreRecommendations({
            from,
            canSize,
            canId,
            fetchMore
          }),
          items
        };
      },
      options: props => {
        const { preloadLimit } = props;
        const locale = get(props, 'userInfo.locale', DEFAULT_LOCALE);

        return {
          variables: {
            blacklist: config('blacklistName'),
            limit: preloadLimit,
            locale,
            experience: props.experienceTitle || DEFAULT_EXPERIENCE_TITLE,
            method: 'guruBackoff',
            from: 0
          },
          errorPolicy: 'all',
          ssr: true,
          notifyOnNetworkStatusChange: true
        };
      }
    }
  )(Component);

withWorkRecommendations.displayName = 'withWorkRecommendations';

export const useWorkRecommendations = ({
  fragments,
  limit,
  userInfo,
  ssr = false
}) => {
  const locale = get(userInfo, 'locale', DEFAULT_LOCALE);
  const query = workRecommendationsQuery(fragments);

  const { data, error, networkStatus, refetch, fetchMore } = useQuery(query, {
    variables: {
      blacklist: config('blacklistName'),
      limit,
      locale,
      experience: DEFAULT_EXPERIENCE_TITLE,
      method: 'guruBackoff',
      from: 0
    },
    errorPolicy: 'all',
    ssr,
    notifyOnNetworkStatusChange: true
  });

  const currentUserId = get(userInfo, 'userId');
  const works = get(data, 'cannedWorkRecommendations.recommendations', [])
    .filter(workIsVisible(currentUserId))
    .map(({ work }) => work);

  const canId = get(data, 'cannedWorkRecommendations.canId');
  const canSize = get(data, 'cannedWorkRecommendations.canSize', 0);
  const hasMore = get(data, 'cannedWorkRecommendations.hasMore');
  const from = get(data, 'cannedWorkRecommendations.nextFrom') || limit;

  return {
    works,
    canId,
    error,
    loading: [LOADING, REFETCH].includes(networkStatus),
    fetchingMore: networkStatus === FETCH_MORE,
    refetch,
    hasMore,
    fetchMore: fetchMoreRecommendations({ from, canSize, canId, fetchMore })
  };
};

export { updateQuery as mergeRecommendations, fetchMoreRecommendations };
export default withWorkRecommendations;
